/**
 * ContainerTimelineDialog — single-pane chronological history for a container,
 * triggered from the right-click menu on a container number.
 *
 * Thin wrapper: handles the data fetch + dialog shell. The actual list view
 * lives in `ContainerTimelineList` and is shared with the in-load Event Logs
 * tab of `EditLoadSheet` / `EditExportSheet`.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Clock } from 'lucide-react';
import { ContainerTimelineList } from './ContainerTimelineList';

export const ContainerTimelineDialog = ({ open, onOpenChange, shipment, api }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !shipment?.id) return;
    let cancelled = false;
    // Reset state in a microtask so react-hooks/set-state-in-effect stays happy
    // (deferred off the synchronous effect path; still flushes before paint).
    Promise.resolve().then(() => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
      setEvents([]);
    });
    api.get(`/shipments/${shipment.id}/timeline`)
      .then((res) => {
        if (cancelled) return;
        setEvents(Array.isArray(res.data) ? res.data : []);
        setError(null);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'Failed to load timeline');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shipment?.id, api]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-slate-900 border-slate-700 text-slate-100"
        data-testid="container-timeline-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            <Clock className="h-5 w-5 text-blue-400" />
            Container Timeline
            <Badge variant="outline" className="ml-2 font-mono text-xs border-slate-600">
              {shipment?.container_number || '—'}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Chronological history of every change, dispatch, status update, and comment.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-2 -mr-2">
          <ContainerTimelineList events={events} loading={loading} error={error} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContainerTimelineDialog;
